import type { ProcedureDeclaration, ProcedureInput, ProcedureOutput, TrpcContract } from "@amtana/module";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { createTRPCUntypedClient, httpLink } from "@trpc/client";

import { isTermsRefusal } from "./terms-refusal.ts";

// The browser's wire. The client is derived from the module's contract, never
// from a router type, so the browser never compiles the server. One tRPC call
// per request; the server refuses batches.

/** A read's state. Clients annotate their hooks with it so consumers never resolve query internals. */
export type QueryResult<TData> = UseQueryResult<TData>;

/** A write's state, with `run` resolving to its output. */
export type MutationResult<TOutput, TInput> = UseMutationResult<TOutput, unknown, TInput> & {
  readonly run: (input: TInput) => Promise<TOutput>;
};

/** The framework's one retry, never for a refusal that asking again cannot change. */
const retryOnce = (failures: number, error: Error): boolean => !isTermsRefusal(error) && failures < 1;

const client = createTRPCUntypedClient({ links: [httpLink({ url: "/api/trpc" })] });

type ProcedureNames<TContract extends TrpcContract, TKind extends ProcedureDeclaration["kind"]> = {
  [K in keyof TContract["procedures"]]: TContract["procedures"][K]["kind"] extends TKind ? K : never;
}[keyof TContract["procedures"]] &
  string;

export function useContractMutation<
  TContract extends TrpcContract,
  TName extends ProcedureNames<TContract, "mutation">,
>(
  contract: TContract,
  name: TName,
): UseMutationResult<
  ProcedureOutput<TContract["procedures"][TName]>,
  unknown,
  ProcedureInput<TContract["procedures"][TName]>
> {
  const path = `${contract.namespace}.${name}`;
  return useMutation({
    mutationKey: [path],
    mutationFn: (input: ProcedureInput<TContract["procedures"][TName]>) =>
      client.mutation(path, input) as Promise<ProcedureOutput<TContract["procedures"][TName]>>,
  });
}

export function useContractQuery<
  TContract extends TrpcContract,
  TName extends ProcedureNames<TContract, "query">,
>(
  contract: TContract,
  name: TName,
  request: {
    readonly input: ProcedureInput<TContract["procedures"][TName]>;
    /** Refetch on this interval (ms) while the screen shows something that changes by itself. */
    readonly refetchInterval?: number | false;
    readonly enabled?: boolean;
    /**
     * The screen can do without it (a count in a sidebar, a search box): its
     * failure stays on the result for the caller to show. Otherwise a read that
     * fails before it has data throws to the screen's boundary (ADR-001, point 6).
     */
    readonly optional?: boolean;
  },
): UseQueryResult<ProcedureOutput<TContract["procedures"][TName]>> {
  const { input, ...options } = request;
  const path = `${contract.namespace}.${name}`;
  return useQuery({
    queryKey: [path, input],
    queryFn: ({ signal }) =>
      client.query(path, input, { signal }) as Promise<ProcedureOutput<TContract["procedures"][TName]>>,
    refetchInterval: options.refetchInterval ?? false,
    enabled: options.enabled ?? true,
    placeholderData: (previous) => previous,
    // An optional read refused (signed out, say) is an answer, not a blip: asking again changes nothing.
    retry: options.optional ? false : retryOnce,
    throwOnError: (error, query) =>
      !options.optional && query.state.data === undefined && !isTermsRefusal(error),
  });
}

/** A list read a page at a time (S12.04): every item loaded so far, in order, and a way to load
 * the next page. */
export type PagesResult<TItem> = {
  readonly items: readonly TItem[];
  /** The first page has not arrived yet. */
  readonly isLoading: boolean;
  /** Another page is there to load. */
  readonly hasMore: boolean;
  readonly loadingMore: boolean;
  readonly loadMore: () => void;
};

type ItemOf<TOutput> = TOutput extends { readonly items: ReadonlyArray<infer TItem> } ? TItem : never;

/**
 * A paged read: the procedure takes `{ cursor?, ...input }` and answers `{ items, nextCursor }`;
 * this asks for the first page, then each next one when `loadMore` is called, and hands back
 * every item loaded so far. Invalidating the contract reloads every page already shown.
 */
export function useContractPages<
  TContract extends TrpcContract,
  TName extends ProcedureNames<TContract, "query">,
>(
  contract: TContract,
  name: TName,
  request: {
    readonly input: Omit<ProcedureInput<TContract["procedures"][TName]>, "cursor">;
    readonly refetchInterval?: number | false;
    readonly enabled?: boolean;
  },
): PagesResult<ItemOf<ProcedureOutput<TContract["procedures"][TName]>>> {
  type Output = ProcedureOutput<TContract["procedures"][TName]>;
  type Item = ItemOf<Output>;
  type Paged = { readonly items: readonly Item[]; readonly nextCursor: string | null };
  const { input } = request;
  const path = `${contract.namespace}.${name}`;
  const query = useInfiniteQuery({
    queryKey: [path, input, "pages"],
    queryFn: ({ pageParam, signal }) =>
      client.query(path, pageParam ? { ...input, cursor: pageParam } : input, { signal }) as Promise<Paged>,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: Paged) => last.nextCursor ?? undefined,
    refetchInterval: request.refetchInterval ?? false,
    enabled: request.enabled ?? true,
    placeholderData: (previous) => previous,
    retry: retryOnce,
    throwOnError: (error, cached) => cached.state.data === undefined && !isTermsRefusal(error),
  });
  return {
    items: query.data?.pages.flatMap((page) => page.items) ?? [],
    isLoading: query.isLoading,
    hasMore: query.hasNextPage,
    loadingMore: query.isFetchingNextPage,
    loadMore: () => void query.fetchNextPage(),
  };
}

/**
 * After a write, marks every cached read of the given contracts stale so the
 * screen refetches. A mutation names the namespaces its write can change.
 */
export function useInvalidateContract(): (...contracts: readonly TrpcContract[]) => Promise<void> {
  const queryClient = useQueryClient();
  return async (...contracts) => {
    // No contract named: the write can change anything (a project switch), so refresh every read.
    if (contracts.length === 0) {
      await queryClient.invalidateQueries();
      return;
    }
    const namespaces = new Set(contracts.map((contract) => `${contract.namespace}.`));
    await queryClient.invalidateQueries({
      predicate: (query) => {
        const path = query.queryKey[0];
        return typeof path === "string" && [...namespaces].some((namespace) => path.startsWith(namespace));
      },
    });
  };
}
